import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import useSWR, { mutate } from "swr"
import useSWRMutation from "swr/mutation"

export const invalidateNotifications = () =>
    mutate((key) => Array.isArray(key) && key[0] === "get-notifications")

const getNotifications = ({ orgId }: { orgId: string }) => {
    return fetchApiGateway("get /org/:orgId/notifications", {
        params: { orgId },
    })
}

export const useNotifications = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWR(
        enabled && orgId ? ["get-notifications", orgId] : null,
        ([, orgId]) => getNotifications({ orgId }),
        {
            revalidateOnMount: true,
            revalidateIfStale: true,
            revalidateOnFocus: true,
            keepPreviousData: true,
            dedupingInterval: 0,
        }
    )

    const unreadCount = useMemo(
        () => data?.filter((notification) => !notification.readAt).length ?? 0,
        [data]
    )

    return { notifications: data, isLoading, unreadCount }
}

const markNotificationRead = ({
    notificationId,
    orgId,
}: {
    orgId: string
    notificationId: string
}) => {
    return fetchApiGateway("post /org/:orgId/notifications/:notificationId/read", {
        params: { orgId, notificationId },
    })
}

export const useMarkNotificationRead = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "mark-notification-read",
        (key, { arg: notificationId }: { arg: string }) =>
            orgId ? markNotificationRead({ notificationId, orgId }) : undefined,
        { onSuccess: invalidateNotifications }
    )

    return { markNotificationRead: trigger, isMutating }
}

const markAllNotificationsRead = ({ orgId }: { orgId: string }) => {
    return fetchApiGateway("post /org/:orgId/notifications/read-all", {
        params: { orgId },
    })
}

export const useMarkAllNotificationsRead = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "mark-all-notifications-read",
        () => (orgId ? markAllNotificationsRead({ orgId }) : undefined),
        { onSuccess: invalidateNotifications }
    )

    return { markAllNotificationsRead: trigger, isMutating }
}
